import { useEffect, useState } from "react";
import { useUrlState } from "@/lib/navState";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { label, formatDate } from "@/lib/constants";
import { MapPin, Search, ArrowLeft, Clock, User, Wrench, Car, History as HistoryIcon, ChevronRight } from "lucide-react";

function Shell({ children }) {
  return (
    <div className="ti-shell -mx-5 -my-5 min-h-[calc(100vh-3.25rem)] px-5 py-6 sm:-mx-8 sm:-my-8 sm:px-8 sm:py-8">
      {children}
    </div>
  );
}

function Field({ k, v, gold }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider ti-muted">{k}</div>
      <div className={`text-sm font-semibold ${gold ? "ti-gold" : "text-white"}`}>{v || "—"}</div>
    </div>
  );
}

function ActiveJobSearch() {
  const [q, setQ] = useUrlState("q", "");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try { const { data } = await api.get("/tracking/search", { params: { q: q.trim() } }); setRes(data); }
    catch (e) { setRes({ status: "NOT_FOUND" }); }
    finally { setLoading(false); }
  };

  const st = res?.status;
  const c = res?.claim;

  return (
    <Shell>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] ti-gold"><MapPin size={13} /> Unit Tracking</div>
      <h1 className="mt-1 font-heading text-2xl font-extrabold text-white sm:text-3xl">Lacak Posisi Kendaraan</h1>
      <p className="mt-1 text-sm ti-muted">Hanya menampilkan pekerjaan aktif saat ini. Untuk rekam jejak lengkap gunakan Vehicle History.</p>

      <div className="mt-6 flex max-w-xl gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5B6B87]" />
          <input
            data-testid="tracking-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
            placeholder="Nopol / Vehicle ID / Claim ID / WO / Customer / SPK…"
            className="h-11 w-full rounded-lg border ti-hair bg-[#0E1729] pl-9 pr-3 text-sm text-[#E7ECF3] placeholder:text-[#5B6B87] focus:border-[#3B82F6]/60 focus:outline-none"
          />
        </div>
        <button data-testid="tracking-go" onClick={run} className="rounded-lg ti-gold-bg px-5 text-sm font-bold text-[#0A101F]">Cari</button>
      </div>

      {loading && <div className="ti-card mt-6 h-40 animate-pulse opacity-40" />}

      {!loading && st === "ACTIVE" && (
        <div data-testid="tracking-active" className="ti-card ti-fade mt-6 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="ti-plate text-base">{c.license_plate}</span>
              <div>
                <div className="font-heading text-lg font-bold text-white">{c.vehicle || "—"}</div>
                <div className="text-sm ti-muted">{c.customer_name || "—"}</div>
              </div>
            </div>
            <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-300">● {label(c.physical_status)}</span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <Field k="Claim ID" v={c.claim_no} gold />
            <Field k="WO Number" v={c.wo_number} gold />
            <Field k="Insurance" v={c.insurance_company} />
            <Field k="Current Stage" v={label(c.current_stage)} />
            <Field k="Parts" v={c.parts_status} />
            <Field k="Module" v={label(c.current_module)} />
            <Field k="Target Delivery" v={c.target_delivery ? formatDate(c.target_delivery) : "Belum Ditentukan"} />
            <Field k="SA" v={c.assigned_sa} />
          </div>
          <div className="mt-4 flex items-center justify-between border-t ti-hair pt-4">
            <span className="text-xs ti-muted">Kunjungan sebelumnya: <b className="text-white">{res.previous_visits}</b></span>
            <div className="flex gap-2">
              <button data-testid="tracking-view-timeline" onClick={() => navigate(`/tracking/${c.id}`)} className="rounded-lg border ti-hair bg-[#0E1729] px-4 py-2 text-xs font-semibold text-[#C7D2E5] hover:border-[#3B82F6]/50">Timeline Claim Ini</button>
              <button data-testid="tracking-view-history" onClick={() => navigate(`/history?vehicle=${res.vehicle_id}`)} className="flex items-center gap-1.5 rounded-lg ti-gold-bg px-4 py-2 text-xs font-bold text-[#0A101F]"><HistoryIcon size={13} /> View Vehicle History</button>
            </div>
          </div>
        </div>
      )}

      {!loading && st === "NOT_IN_WORKSHOP" && (
        <div data-testid="tracking-not-in-workshop" className="ti-card ti-fade mt-6 p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E1729]"><Car size={22} className="ti-muted" /></span>
            <div>
              <div className="font-heading text-lg font-bold text-white">CURRENT STATUS: NOT IN WORKSHOP</div>
              <div className="text-sm ti-muted">Kendaraan tidak sedang dikerjakan. Semua pekerjaan sebelumnya sudah selesai.</div>
            </div>
          </div>
          {res.last_visit && (
            <div className="mt-5 grid grid-cols-2 gap-4 border-t ti-hair pt-4 sm:grid-cols-3">
              <Field k="Last Claim" v={res.last_visit.claim_no} gold />
              <Field k="Last WO" v={res.last_visit.wo_number} gold />
              <Field k="Last Unit Out" v={res.last_visit.unit_out_date ? formatDate(res.last_visit.unit_out_date) : "—"} />
            </div>
          )}
          <div className="mt-4 flex justify-end border-t ti-hair pt-4">
            <button data-testid="tracking-view-history" onClick={() => navigate(`/history?vehicle=${res.vehicle_id}`)} className="flex items-center gap-1.5 rounded-lg ti-gold-bg px-4 py-2 text-xs font-bold text-[#0A101F]"><HistoryIcon size={13} /> View Previous History</button>
          </div>
        </div>
      )}

      {!loading && (st === "NOT_FOUND") && (
        <div data-testid="tracking-not-found" className="ti-card mt-6 flex flex-col items-center gap-2 py-14 text-center">
          <Wrench size={30} className="ti-muted" />
          <p className="font-heading text-base font-semibold text-white">Tidak Ditemukan</p>
          <p className="text-sm ti-muted">Tidak ada kendaraan/claim yang cocok dengan pencarian.</p>
        </div>
      )}
    </Shell>
  );
}

function Timeline({ id }) {
  const [claim, setClaim] = useState(null);
  const [history, setHistory] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/claims/${id}`).then((r) => setClaim(r.data)).catch(() => setClaim(false));
    api.get(`/claims/${id}/history`).then((r) => setHistory((r.data || []).slice().reverse())).catch(() => setHistory([]));
  }, [id]);

  if (claim === false) return <Shell><div className="ti-card p-5 text-sm text-red-300">Unit tidak ditemukan.</div></Shell>;

  return (
    <Shell>
      <button data-testid="tracking-back" onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm ti-muted transition-colors hover:text-white">
        <ArrowLeft size={15} /> Kembali
      </button>

      <div className="ti-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="ti-plate text-base">{claim?.license_plate || "—"}</span>
            <div>
              <div className="font-heading text-lg font-bold text-white">{[claim?.vehicle_brand, claim?.vehicle_model].filter(Boolean).join(" ") || "—"}</div>
              <div className="text-sm ti-muted">{claim?.customer_name || "—"} · {claim?.insurance_company || "Umum"}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {claim?.claim_no && <span className="rounded-full bg-[#111C33] px-3 py-1 text-xs font-semibold ti-gold">{claim.claim_no}</span>}
            {claim?.wo_number && <span className="rounded-full bg-[#111C33] px-3 py-1 text-xs font-semibold ti-gold">WO {claim.wo_number}</span>}
            <span className="rounded-full border border-[#3B82F6]/40 bg-[#111C33] px-3 py-1 text-xs font-semibold text-white">{label(claim?.status)}</span>
          </div>
        </div>
      </div>

      <div className="mb-3 mt-8 flex items-center gap-3">
        <span className="h-4 w-1 rounded-full ti-gold-bg" />
        <h2 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-[#C7D2E5]">Timeline Pekerjaan (Claim / WO ini saja)</h2>
      </div>

      {history && history.length === 0 && <div data-testid="timeline-empty" className="ti-card p-5 text-sm ti-muted">Belum ada riwayat untuk claim ini.</div>}

      <div className="relative ml-1">
        {(history || []).map((h, i) => {
          const isLast = i === history.length - 1;
          return (
            <div key={h.id || i} data-testid={`timeline-item-${i}`} className="relative flex gap-4 pb-6">
              {!isLast && <span className="absolute left-[7px] top-4 h-full w-px bg-[#1E2A45]" />}
              <span className={`relative z-10 mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${i === 0 ? "border-[#60A5FA] ti-gold-bg" : "border-[#1E2A45] bg-[#0E1729]"}`} />
              <div className="ti-card flex-1 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white">{h.previous_status ? `${label(h.previous_status)} → ` : ""}{label(h.new_status)}</span>
                  <span className="flex items-center gap-1 text-xs ti-muted"><Clock size={12} /> {formatDate(h.date)} {h.time}</span>
                </div>
                {h.notes && <p className="mt-1 text-sm text-[#B7C3D8]">{h.notes}</p>}
                <div className="mt-2 flex items-center gap-3 text-xs ti-muted">
                  <span className="flex items-center gap-1"><User size={12} /> {h.user || "—"}</span>
                  {h.module && <span>· {label(h.module)}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}

export default function UnitTracking() {
  const { id } = useParams();
  return id ? <Timeline id={id} /> : <ActiveJobSearch />;
}
